// Hook: useGraphWiring.js
// Purpose: The one Strategy Builder graph wiring authority shared by Canvas, Tree and Properties panel. isValidConnection, onConnect,
//          handleEdgesChange and deleteNode are main's StrategyLogicStep canvas code moved verbatim (only the graph
//          state now arrives through the hook arguments); validateConnection explains the same refusals in words.

import { useCallback } from 'react';
import { LOGIC_NODE_TYPES } from '../config/nodeVisualConfig';
import {
  canonicalSourceHandle,
  linkOperandInput,
  materializeConditionTargetSlot,
  matchesTargetInput,
  nextInputSlotId,
  resolveTargetHandle,
  setOperandConstant,
  targetRequiresExistingInputSlot,
} from '../utils/operandLinking';

export const LINK_REFUSAL_TEXT = Object.freeze({
  missing_item: 'That authoring item is no longer available.',
  missing_node: 'The source or target node no longer exists.',
  missing_edge: 'The dragged connection is no longer available.',
  node_creation_failed: 'The node could not be created.',
  connection_not_allowed: 'That connection is not allowed.',
  operand_link_failed: 'That operand cannot be linked.',
  operand_target_unavailable: 'The operand target is unavailable.',
  missing_indicator: 'That indicator is no longer available.',
  indicator_creation_failed: 'The indicator could not be created.',
  unsupported_operand: 'That operand choice is not supported.',
  operator_unavailable: 'That comparison operator is not available.',
  locked_delete: 'Locked nodes cannot be deleted.',
  authoring_drop_target: 'Drop a Toolbox item on a section heading or attachable row.',
  tree_authoring_unavailable: 'Tree authoring is unavailable.',
  item_add_failed: 'That item could not be added.',
  dragged_operand_unavailable: 'The dragged operand is unavailable.',
  connection_drop_target: 'Drop on a condition input or logic group.',
  locked_node: 'Locked nodes cannot be connected.',
  self_connection: 'A node cannot connect to itself.',
  cycle: 'That connection would create a loop.',
  chain_depth: 'Indicator chain depth limit (5) exceeded.',
  boolean_source_required: 'This input accepts only a condition or logic result.',
  slot_missing: 'The target input slot no longer exists.',
  no_free_slot: 'The target has no free input slot.',
});

/*>>> PURE GRAPH PREDICATES <<<*/
//--> true when `goalId` already feeds `startId` (walk inbound edges upstream). Adding startId -> goalId would loop.
const feedsInto = (startId, goalId, edges) => {
  const stack = [startId];
  const seen = new Set();
  while (stack.length > 0) {
    const id = stack.pop();
    if (id === goalId) return true;
    if (!seen.has(id)) {
      seen.add(id);
      edges.forEach((edge) => { if (edge.target === id) stack.push(edge.source); });
    }
  }
  return false;
};

//--> self-edge or cycle refusal (a pure predicate over the given edges)
const loopReason = (connection, edges) => {
  if (connection.source === connection.target) return LINK_REFUSAL_TEXT.self_connection;
  if (feedsInto(connection.source, connection.target, edges)) return LINK_REFUSAL_TEXT.cycle;
  return '';
};

const refusal = (code, reason) => ({ valid: false, code, reason });

const validateEdgeRemovals = (nodes, edges, edgeIds) => {
  const requestedEdgeIds = new Set(edgeIds);
  const lockedNodeIds = new Set(
    nodes.filter(node => node.connectable === false).map(node => node.id)
  );
  const removesLockedEdge = edges.some(edge => (
    requestedEdgeIds.has(edge.id)
    && (lockedNodeIds.has(edge.source) || lockedNodeIds.has(edge.target))
  ));
  return removesLockedEdge
    ? { ok: false, reason: LINK_REFUSAL_TEXT.locked_node }
    : { ok: true, reason: '' };
};

/**
 * The single graph mutation seam shared by the Canvas and authoring tree.
 * Callers own graph state (nodes are the role-locked canvas projection); this hook owns validation and
 * node-data synchronization, exactly as main's canvas did.
 */
const useGraphWiring = ({
  nodes,
  edges,
  setNodes,
  setEdges,
  onNodesChange,
  onEdgesChange,
}) => {
  // --> Calculate indicator chain depth to prevent runaway recursion
  const getChainDepth = useCallback((nodeId, currentEdges, visited = new Set(), depth = 0) => {
    // Prevent infinite loops with visited set
    if (visited.has(nodeId)) return depth;
    if (depth > 5) return depth; // Early exit for depth limit

    visited.add(nodeId);
    const parentEdges = currentEdges.filter(e => e.target === nodeId);

    if (parentEdges.length === 0) return depth;

    // Find max depth from all parent chains
    return Math.max(...parentEdges.map(e =>
      getChainDepth(e.source, currentEdges, new Set(visited), depth + 1)
    ));
  }, []);


  /**
   * Validates connections between nodes.
   * Logic Gates (AND, OR, NOT) should only accept boolean inputs from Comparators or other Logic Gates.
   */
  const isValidConnection = useCallback((connection, {
    nodesSnapshot = nodes,
    edgesSnapshot = edges,
  } = {}) => {
    const targetNode = nodesSnapshot.find(n => n.id === connection.target);
    const sourceNode = nodesSnapshot.find(n => n.id === connection.source);

    // Rule: Logic Gates and Signal Nodes ONLY accept Comparator/Logic Gate/Specialized Signal outputs
    if (['logic_gate', 'signal', 'position_trigger'].includes(targetNode?.type)) {
      const allowedSignalActions = ['sl', 'tp', 'time_exit', 'enter_long', 'enter_short', 'buy', 'sell'];
      const isBooleanSource = LOGIC_NODE_TYPES.includes(sourceNode?.type) ||
                             (['signal', 'position_trigger'].includes(sourceNode?.type) && allowedSignalActions.includes(sourceNode?.data?.action));
      if (!isBooleanSource) {
        console.warn(`[DIAG] [Connection-Validation] ${targetNode.type} ${targetNode.id} only accepts boolean inputs (Comparators, Logic Gates, Time Windows, or Signal Nodes). Source ${sourceNode?.id} (${sourceNode?.type}) is invalid.`);
        return false;
      }
    }

    //* 50-20: self-edges and cycles are refused on every surface (added to main's rule set)
    if (loopReason(connection, edgesSnapshot)) return false;

    // Default: Allow all other connections (e.g., Comparators taking Math/Indicators)
    return true;
  }, [nodes, edges]);

  /*>>> REFUSAL REASONS (no mutation) <<<*/
  //* The same refusals onConnect / isValidConnection apply, as {valid, reason}, over the edges minus
  //  ignoreEdgeIds (the edge a Tree move removes first). Explicit snapshots support validate-before-create.
  const validateConnection = useCallback((params, {
    ignoreEdgeIds = [],
    nodesSnapshot = nodes,
    edgesSnapshot = edges,
  } = {}) => {
    const targetNode = nodesSnapshot.find(n => n.id === params?.target);
    const sourceNode = nodesSnapshot.find(n => n.id === params?.source);
    if (!targetNode || !sourceNode) {
      return refusal('missing_node', LINK_REFUSAL_TEXT.missing_node);
    }
    if (targetNode.connectable === false || sourceNode.connectable === false) {
      return refusal('locked_node', LINK_REFUSAL_TEXT.locked_node);
    }
    const ignored = new Set(ignoreEdgeIds);
    const remainingEdges = edgesSnapshot.filter(edge => !ignored.has(edge.id));
    const loop = loopReason(params, remainingEdges);
    if (loop) return refusal(params.source === params.target ? 'self_connection' : 'cycle', loop);
    if (targetNode.type === 'indicator' && sourceNode.type === 'indicator'
      && getChainDepth(params.source, remainingEdges) + 1 > 5) {
      return refusal('chain_depth', LINK_REFUSAL_TEXT.chain_depth);
    }
    if (!isValidConnection(params, { nodesSnapshot, edgesSnapshot: remainingEdges })) {
      return refusal('boolean_source_required', LINK_REFUSAL_TEXT.boolean_source_required);
    }
    return { valid: true, code: 'ok', reason: '' };
  }, [nodes, edges, getChainDepth, isValidConnection]);

  /**
   * Handles new edge connections in the strategy graph.
   *
   * CRITICAL: This function synchronizes the target node's data.sourceId with the visual edge
   * to prevent "ghost connection" bugs where UI node properties and backend graph calculations diverge.
   *
   * Also validates indicator chain depth to prevent runaway recursive calculations.
   *
   * @param {Object} params - ReactFlow connection parameters
   * @param {string} params.source - Source node ID
   * @param {string} params.target - Target node ID
   * @param {Object} options - Optional atomic-replacement inputs and explicit graph snapshots
   */
  const onConnect = useCallback((params, {
    removeEdgeIds = [],
    edgeStyle,
    nodesSnapshot = nodes,
    edgesSnapshot = edges,
  } = {}) => {
    const removedEdgeIds = new Set(removeEdgeIds);
    const explicitRemovalValidation = validateEdgeRemovals(
      nodesSnapshot,
      edgesSnapshot,
      removedEdgeIds,
    );
    if (!explicitRemovalValidation.ok) {
      return { ok: false, reason: explicitRemovalValidation.reason, params: null };
    }
    const postRemovalEdges = edgesSnapshot.filter(edge => !removedEdgeIds.has(edge.id));
    const snapshotTargetNode = nodesSnapshot.find(n => n.id === params?.target);
    const requestedTargetHandle = resolveTargetHandle(
      snapshotTargetNode,
      params?.targetHandle,
      params?.targetHandle === undefined || params?.targetHandle === null || params?.targetHandle === '' ? 0 : null,
    );
    const removedTargetHandles = new Set(edgesSnapshot
      .filter(edge => removedEdgeIds.has(edge.id) && edge.target === params?.target)
      .map(edge => resolveTargetHandle(
        snapshotTargetNode,
        edge.targetHandle,
        edge.targetHandle === undefined || edge.targetHandle === null || edge.targetHandle === '' ? 0 : null,
      ))
      .filter(handle => handle !== null && handle !== requestedTargetHandle));
    const storedTargetNode = removedTargetHandles.size > 0
      && ['signal', 'position_trigger'].includes(snapshotTargetNode?.type)
      && Array.isArray(snapshotTargetNode.data?.inputs)
      ? {
        ...snapshotTargetNode,
        data: {
          ...snapshotTargetNode.data,
          inputs: snapshotTargetNode.data.inputs.filter(input => !removedTargetHandles.has(input?.id)),
        },
      }
      : snapshotTargetNode;
    const postRemovalNodes = storedTargetNode === snapshotTargetNode
      ? nodesSnapshot
      : nodesSnapshot.map(node => (node.id === storedTargetNode.id ? storedTargetNode : node));
    const targetNode = materializeConditionTargetSlot(storedTargetNode, params?.targetHandle);
    const targetSlotMaterialized = targetNode !== storedTargetNode;
    const handleMissing = params?.targetHandle === undefined
      || params?.targetHandle === null
      || params?.targetHandle === '';
    const resolvedTargetHandle = resolveTargetHandle(
      targetNode,
      params?.targetHandle,
      handleMissing ? 0 : null,
    );
    const resolvedParams = {
      ...params,
      sourceHandle: canonicalSourceHandle(params?.sourceHandle),
      targetHandle: resolvedTargetHandle
        ?? (handleMissing && !targetRequiresExistingInputSlot(targetNode) ? 'input_0' : null),
    };
    const validation = validateConnection(resolvedParams, {
      nodesSnapshot: postRemovalNodes,
      edgesSnapshot: postRemovalEdges,
    });
    if (!validation.valid) return { ok: false, reason: validation.reason, params: null };
    if (targetRequiresExistingInputSlot(targetNode) && resolvedParams.targetHandle === null) {
      return { ok: false, reason: LINK_REFUSAL_TEXT.slot_missing, params: null };
    }

    const sourceNode = postRemovalNodes.find(n => n.id === resolvedParams.source);

    // --> Auto-Append Logic for Signal Exits
    // If user drops an edge onto an existing handle (e.g. input_0) that already has a connection,
    // we automatically append a new slot instead of overwriting the previous entry.
    let finalTargetHandle = resolvedParams.targetHandle;
    let targetNodeForWrite = targetNode;
    let targetInputsForWrite = targetSlotMaterialized || storedTargetNode !== snapshotTargetNode
      ? targetNode.data.inputs
      : null;

    if (targetNode?.type === 'signal' || targetNode?.type === 'position_trigger') {
        const currentInputs = Array.isArray(targetNode.data.inputs) ? targetNode.data.inputs : [];
        const isOccupied = postRemovalEdges.some(e => (
          e.target === resolvedParams.target
          && resolveTargetHandle(
            targetNode,
            e.targetHandle,
            e.targetHandle === undefined || e.targetHandle === null || e.targetHandle === '' ? 0 : null,
          ) === finalTargetHandle
        ));

        if (isOccupied) {
            const existingSlots = currentInputs.length > 0
              ? currentInputs
              : [{ id: finalTargetHandle, type: 'link', offset: 0 }];
            finalTargetHandle = nextInputSlotId(existingSlots);
            targetInputsForWrite = existingSlots.concat({ id: finalTargetHandle, type: 'link', offset: 0 });
            targetNodeForWrite = {
              ...targetNode,
              data: {
                ...targetNode.data,
                inputs: targetInputsForWrite,
              },
            };
        }
    }

    const finalParams = { ...resolvedParams, targetHandle: finalTargetHandle };
    const isImplicitReplacement = (edge) => {
      if (edge.target !== finalParams.target) return false;
      if (targetNode?.type === 'indicator') return true;
      const edgeTargetHandle = resolveTargetHandle(
        targetNode,
        edge.targetHandle,
        edge.targetHandle === undefined || edge.targetHandle === null || edge.targetHandle === '' ? 0 : null,
      );
      if (targetNode?.type === 'signal' || targetNode?.type === 'position_trigger') {
        return edge.source === finalParams.source && edgeTargetHandle === finalTargetHandle;
      }
      return edgeTargetHandle === finalTargetHandle;
    };
    const implicitRemovalEdgeIds = postRemovalEdges.filter(isImplicitReplacement).map(edge => edge.id);
    const plannedRemovalEdgeIds = new Set([...removedEdgeIds, ...implicitRemovalEdgeIds]);
    const removalValidation = validateEdgeRemovals(
      nodesSnapshot,
      edgesSnapshot,
      implicitRemovalEdgeIds,
    );
    if (!removalValidation.ok) {
      return { ok: false, reason: removalValidation.reason, params: null };
    }

    // 1. Update Edges (Visual)
    setEdges((eds) => {
      const filtered = eds.filter(edge => !plannedRemovalEdgeIds.has(edge.id));

      return linkOperandInput({
        edges: filtered,
        inputs: [],
        targetNode: targetNodeForWrite,
        targetId: finalParams.target,
        targetHandle: finalTargetHandle,
        sourceId: finalParams.source,
        sourceHandle: finalParams.sourceHandle,
        persistInput: false,
        replaceTargetInput: false,
        edgeId: finalParams.id || `reactflow__edge-${finalParams.source}${finalParams.sourceHandle}-${finalParams.target}${finalTargetHandle}`,
        edgeStyle: edgeStyle || {
          strokeWidth: 2,
          stroke: [...LOGIC_NODE_TYPES, 'signal', 'position_trigger'].includes(targetNode?.type)
            ? 'var(--theme-purple)'
            : 'var(--color-accent)',
        },
      }).edges;
    });

    // 2. Update Target Node Data (Logical Sync)
    // This fixes the "Ghost Connection" issue where visual edge != node data
    setNodes((nds) => nds.map((node) => {
      if (node.id === finalParams.target) {
        const isEdgeOnlyAssociation = finalTargetHandle === 'trigger_input';
        let newData = isEdgeOnlyAssociation
          ? { ...node.data }
          : {
            ...node.data,
            sourceId: finalParams.source,
            input: finalParams.source // Legacy sync
          };

        // Keep indicator timeframe synced with its source. The source may be a data_source OR
        // another indicator (e.g. a slope built on top of a T3) — in both cases the new indicator
        // must inherit the source's timeframe, not keep its own default. Previously only the
        // data_source case synced, so an indicator sourced from another indicator kept a stale tf
        // (the '5m slope on a 1m T3' class). The backend gate (Phase 2.5c) enforces this on save;
        // this keeps the builder visually correct at connect time.
        if (node.type === 'indicator'
            && (sourceNode?.type === 'data_source' || sourceNode?.type === 'indicator')
            && sourceNode?.data?.timeframe) {
          const sourceTf = sourceNode.data.timeframe;
          newData = {
            ...newData,
            timeframe: sourceTf,
            params: {
              ...(newData.params || {}),
              timeframe: sourceTf
            }
          };
        }

        // --> Sync inputs array for multi-input nodes (Logic, Math, Signal)
        if (!isEdgeOnlyAssociation
          && ['comparator', 'logic_gate', 'signal', 'position_trigger', 'math'].includes(node.type)) {
          newData.inputs = linkOperandInput({
            inputs: targetInputsForWrite || node.data.inputs,
            edges: [],
            targetNode: targetNodeForWrite,
            targetId: finalParams.target,
            targetHandle: finalTargetHandle,
            sourceId: finalParams.source,
            sourceHandle: finalParams.sourceHandle,
          }).inputs;
        }

        return {
          ...node,
          data: newData
        };
      }
      return node;
    }));
    return { ok: true, reason: '', params: finalParams };
  }, [setEdges, setNodes, nodes, edges, validateConnection]);


  /**
   * Handles edge change events (add, remove, update).
   *
   * Automatically cleans up node.data.sourceId when edges are removed to maintain
   * consistency between visual graph and node data.
   *
   * NOTE: The 'edges' variable in this callback is the pre-update snapshot due to closure scope,
   * which is exactly what we need to identify which nodes were affected before the removal.
   *
   * @param {Array} changes - Array of edge change objects from ReactFlow
   */
  // --> Handle Edge Removals to Auto-Clean Node Data
  const handleEdgesChange = useCallback((changes) => {
      const removals = changes.filter(change => change.type === 'remove');
      const removalValidation = validateEdgeRemovals(
          nodes,
          edges,
          removals.map(change => change.id),
      );
      if (!removalValidation.ok) return removalValidation;
      // 1. Propagate changes to ReactFlow state
      if (onEdgesChange) onEdgesChange(changes);

      // 2. Handle Removals
      if (removals.length > 0) {
           const removedEdgeIds = new Set(removals.map(c => c.id));

           // Find which nodes are losing a connection
           const affectedNodes = new Set();
           // NOTE: 'edges' here is the pre-update snapshot (closure), which we need
           // to identify which nodes were affected before the removal
           const removedEdges = edges.filter(e => removedEdgeIds.has(e.id));
           removedEdges.forEach(e => affectedNodes.add(e.target));

           if (affectedNodes.size > 0) {
               setNodes(nds => nds.map(node => {
                   if (affectedNodes.has(node.id)) {
                       // Identify which edges were removed for THIS specific node
                       const removedEdgesToThisNode = removedEdges.filter(e => e.target === node.id);
                       // Check if there are OTHER incoming edges remaining post-removal
                       const remainingEdges = edges.filter(e => e.target === node.id && !removedEdgeIds.has(e.id));

                       let newInputs = Array.isArray(node.data.inputs) ? [...node.data.inputs] : null;

                       // --> Sync inputs array removal
                       if (newInputs) {
                             removedEdgesToThisNode.forEach(e => {
                               const handleId = e.targetHandle || 'input_0';
                               const normalizedHandleId = String(handleId || 'input_0');
                               let idx = newInputs.findIndex(i => String(i?.id || 'input_0') === normalizedHandleId);
                               if (idx < 0) {
                                 const sourceMatches = newInputs
                                   .map((input, inputIdx) => ({ input, inputIdx }))
                                   .filter(({ input }) => input?.type === 'link' && input?.nodeId === e.source);
                                 if (sourceMatches.length === 1) {
                                   idx = sourceMatches[0].inputIdx;
                                 }
                               }
                               if (idx >= 0) {
                                   if (['signal', 'position_trigger'].includes(node.type)) {
                                       newInputs.splice(idx, 1);
                                   } else {
                                       // Reset through the one operand-constant writer.
                                       newInputs = setOperandConstant({
                                           inputs: newInputs,
                                           edges: [],
                                           targetNode: node,
                                           targetId: node.id,
                                           targetHandle: handleId,
                                           inputIndex: idx,
                                       }).inputs;
                                   }
                               }
                           });
                       }

                       if (remainingEdges.length === 0) {
                           // No more inputs! Clear sourceId and legacy input.
                           return {
                               ...node,
                               data: {
                                   ...node.data,
                                   sourceId: null,
                                   input: null,
                                   inputs: newInputs
                               }
                           };
                       } else {
                           const fallbackSourceId = remainingEdges[0].source;
                           const fallbackSourceNode = nodes.find(n => n.id === fallbackSourceId);

                           let nextData = {
                             ...node.data,
                             sourceId: fallbackSourceId,
                             input: fallbackSourceId,
                             inputs: newInputs
                           };

                           // Keep indicator timeframe synced with fallback data_source.
                           if (node.type === 'indicator' && fallbackSourceNode?.type === 'data_source' && fallbackSourceNode?.data?.timeframe) {
                             const sourceTf = fallbackSourceNode.data.timeframe;
                             nextData = {
                               ...nextData,
                               timeframe: sourceTf,
                               params: {
                                 ...(nextData.params || {}),
                                 timeframe: sourceTf
                               }
                             };
                           }

                           // Fallback to the next available source for legacy fields
                           return {
                               ...node,
                             data: nextData
                           };
                       }
                   }
                   return node;
               }));
           }
      }
      return { ok: true, reason: '' };
              }, [onEdgesChange, edges, nodes, setNodes]);

  const unlinkTarget = useCallback(({
    target,
    targetHandle,
    inputIndex = null,
    persistInput = true,
  }) => {
    const targetNode = nodes.find(node => node.id === target);
    if (!targetNode) return { ok: false, reason: LINK_REFUSAL_TEXT.missing_node };
    const matchingEdges = edges.filter(edge => (
      matchesTargetInput(edge, target, targetHandle, inputIndex)
    ));
    const outcome = handleEdgesChange(
      matchingEdges.map(edge => ({ type: 'remove', id: edge.id })),
    );
    if (!outcome.ok || matchingEdges.length > 0 || !persistInput) return outcome;
    setNodes(current => current.map(node => {
      if (node.id !== target) return node;
      const result = setOperandConstant({
        inputs: node.data?.inputs,
        edges: [],
        targetNode: node,
        targetId: target,
        targetHandle,
        inputIndex,
      });
      return { ...node, data: { ...node.data, inputs: result.inputs } };
    }));
    return outcome;
  }, [nodes, edges, handleEdgesChange, setNodes]);

  // --> main's deleteFromContextMenu node branch, parameterised by node id (Canvas context menu + Tree Delete key)
  //! Atomic role guard (Codex round 3): the node and its edges go together or not at all. If the node, or any edge
  //  touching it, involves a locked (role-protected) node, nothing is removed and the caller gets the reason; on main
  //  the role filter refused the node while its other edges were still removed, detaching logic silently.
  const deleteNode = useCallback((nodeId, rollback = {}) => {
       const createdEdges = Array.isArray(rollback) ? rollback : (rollback.createdEdges || []);
       const lockedIds = new Set(nodes.filter(n => n.connectable === false || n.deletable === false).map(n => n.id));
       const connectedEdges = edges.filter(e => e.source === nodeId || e.target === nodeId);
       const rollbackEdges = createdEdges.filter(e => e.source === nodeId || e.target === nodeId);
       const rollbackEdgeIds = new Set(rollbackEdges.map(edge => edge.id));
       const protectedExistingEdge = connectedEdges
           .filter(edge => !rollbackEdgeIds.has(edge.id))
           .some(edge => lockedIds.has(edge.source) || lockedIds.has(edge.target));
       if (lockedIds.has(nodeId) || protectedExistingEdge) {
           return { ok: false, reason: 'This node is linked to an admin-only model node; only an admin can delete it.' };
       }
       if (onNodesChange) onNodesChange([{ type: 'remove', id: nodeId }]);

       // Clean up connected edges when node is manually deleted via context menu
       if (connectedEdges.length > 0) {
           handleEdgesChange(connectedEdges.map(e => ({ type: 'remove', id: e.id })));
       }
       if (rollbackEdges.length > 0) {
           setEdges(current => current.filter(edge => !rollbackEdgeIds.has(edge.id)));
       }
       return { ok: true, reason: '' };
  }, [handleEdgesChange, onNodesChange, edges, nodes, setEdges]);

  return {
    isValidConnection,
    onConnect,
    handleEdgesChange,
    unlinkTarget,
    deleteNode,
    validateConnection,
  };
};

export default useGraphWiring;
